/**
 * Thin auth adapter that exposes an MSAL-shaped API (`useMsal`,
 * `acquireTokenSilent`, the `*Template` components) backed by Firebase, so the
 * many call sites that consume that surface need no changes.
 *
 * `acquireTokenSilent` returns the Firebase ID token (from
 * `auth.currentUser.getIdToken()`) as both `idToken` and `accessToken`; that
 * token's claims (`sub` = Firebase UID, plus the standard `email`/`name`) are
 * what the backend verifies.
 */

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useFirebaseAuth } from './FirebaseAuthContext';

/** Kept for call-shape compatibility; Firebase ID tokens need no scopes. */
export const loginRequest = {} as const;

/** Subset of `@azure/msal-browser`'s `InteractionStatus` the app referenced. */
export const InteractionStatus = {
  Startup: 'startup',
  Login: 'login',
  Logout: 'logout',
  AcquireToken: 'acquireToken',
  HandleRedirect: 'handleRedirect',
  None: 'none',
} as const;

/** Subset of `@azure/msal-browser`'s `InteractionType` the app referenced. */
export const InteractionType = {
  Redirect: 'redirect',
  Popup: 'popup',
  Silent: 'silent',
  None: 'none',
} as const;

export interface AuthResult {
  idToken: string;
  accessToken: string;
}

export interface CompatAccount {
  idTokenClaims?: { sub?: string };
}

export interface CompatInstance {
  acquireTokenSilent(request?: unknown): Promise<AuthResult>;
  getActiveAccount(): CompatAccount | null;
  loginRedirect(request?: unknown): Promise<void>;
  logoutRedirect(request?: unknown): Promise<void>;
  /** No-op: navigation is handled by react-router in the Firebase flows. */
  setNavigationClient(client?: unknown): void;
}

// Firebase keeps a process-global current user, so the instance methods read
// `auth.currentUser` directly. A single stable instance object is shared by all
// callers — many list `instance` in their `useEffect` deps, so a fresh object
// each render would re-fire those effects in a fetch → setState → re-render loop.
const compatInstance: CompatInstance = {
  async acquireTokenSilent() {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User is not authenticated');
    }
    const token = await user.getIdToken();
    return { idToken: token, accessToken: token };
  },
  getActiveAccount() {
    const user = auth.currentUser;
    return user ? { idTokenClaims: { sub: user.uid } } : null;
  },
  async loginRedirect() {
    window.location.assign('/sign-in');
  },
  async logoutRedirect() {
    await signOut(auth);
    window.location.assign('/');
  },
  setNavigationClient() {
    /* no-op */
  },
};

export function useMsal(): {
  instance: CompatInstance;
  inProgress: string;
  accounts: CompatAccount[];
} {
  const { user, isLoaded } = useFirebaseAuth();

  return {
    instance: compatInstance,
    inProgress: isLoaded ? InteractionStatus.None : InteractionStatus.Startup,
    accounts: user ? [{ idTokenClaims: { sub: user.uid } }] : [],
  };
}

export function useIsAuthenticated(): boolean {
  const { user } = useFirebaseAuth();
  // Anonymous (guest) users are signed into Firebase but are NOT treated as
  // authenticated by the app: the NavBar still shows Sign in / Sign up, and
  // (crucially) `useProjectActions(isAuthenticated)` no-ops every persistence
  // call for them, giving guests a read-only "try it" experience. Guest upload
  // code reads the token directly via `acquireTokenSilent` instead of this.
  return !!user && !user.isAnonymous;
}

/**
 * True when the current user is a Firebase *anonymous* (guest) sign-in. Drives
 * the guest-only UI affordances on the project page (read-only persistence,
 * locked downloads, "sign up to save" banner).
 */
export function useIsGuest(): boolean {
  const { user } = useFirebaseAuth();
  return !!user?.isAnonymous;
}

/**
 * Renders children only when a *real* (non-anonymous) user is signed in
 * (mirrors MSAL's component). Anonymous guests are treated as signed-out so
 * the authenticated chrome (workspace links, account menu) stays hidden.
 */
export function AuthenticatedTemplate({ children }: { children: ReactNode }) {
  const { user } = useFirebaseAuth();
  return user && !user.isAnonymous ? <>{children}</> : null;
}

/**
 * Renders children once Firebase has loaded and no real user is signed in.
 * Anonymous guests count as signed-out here so they still see Sign in / Sign up.
 */
export function UnauthenticatedTemplate({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useFirebaseAuth();
  return isLoaded && (!user || user.isAnonymous) ? <>{children}</> : null;
}

/**
 * Route guard mirroring MSAL's `MsalAuthenticationTemplate`. Renders children
 * only for signed-in users whose email is verified; once Firebase has loaded it
 * redirects signed-out users to `/sign-in` and signed-in-but-unverified users
 * to `/sign-up` (the "verify your email" screen). The latter matters because
 * `createUserWithEmailAndPassword` signs the user in immediately — without this
 * check a freshly registered, unverified account would reach protected routes.
 * Extra MSAL props are accepted and ignored.
 */
export function MsalAuthenticationTemplate({
  children,
}: {
  interactionType?: unknown;
  authenticationRequest?: unknown;
  errorComponent?: unknown;
  loadingComponent?: unknown;
  children?: ReactNode;
}) {
  const { user, isLoaded } = useFirebaseAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      navigate('/sign-in', { replace: true });
    } else if (!user.emailVerified) {
      navigate('/sign-up', { replace: true });
    }
  }, [isLoaded, user, navigate]);

  if (!isLoaded) {
    return (
      <div
        className={`
          flex min-h-[40vh] items-center justify-center font-[Outfit]
          text-gray-500
        `}
      >
        Loading…
      </div>
    );
  }
  if (!user || !user.emailVerified) return null;
  return <>{children}</>;
}
